import React, { useState, useEffect } from 'react';
import { 
  MessageCircle, 
  Send, 
  ThumbsUp, 
  Flag, 
  MoreVertical, 
  Reply, 
  Trash2,
  AlertTriangle,
  CheckCircle,
  Loader2,
  X,
  User
} from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import AuthModal from './AuthModal';

// Motivos de denúncia de comentário (mapear para ReportReasonCode do backend)
const COMMENT_REPORT_REASONS = [
  { value: 'spam', label: 'Spam ou propaganda' },
  { value: 'hate_speech', label: 'Discurso de ódio' },
  { value: 'offensive_language', label: 'Linguagem ofensiva' },
  { value: 'fake_content', label: 'Informação falsa' },
  { value: 'personal_data', label: 'Exposição de dados pessoais' },
  { value: 'other', label: 'Outro' }
];

// Componente de tempo relativo
const TimeAgo = ({ date }) => {
  const timeAgo = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = (now - d) / 1000;
    
    if (diff < 60) return 'agora';
    if (diff < 3600) return `${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} dias`;
    return d.toLocaleDateString('pt-BR');
  };
  
  return <span className="text-xs text-gray-400">{timeAgo(date)}</span>;
};

// Componente de comentário individual
const CommentItem = ({ 
  comment, 
  isOwner, 
  currentUserId,
  onLike, 
  onUnlike,
  onReply, 
  onReport,
  onDelete
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const isLiked = comment.liked_by?.includes(currentUserId);
  const isAuthor = comment.author_id === currentUserId;

  const handleLikeToggle = async () => {
    if (isLiked) {
      await onUnlike(comment.id);
    } else {
      await onLike(comment.id);
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;
    
    setSubmitting(true);
    try {
      await onReply(comment.id, replyContent);
      setReplyContent('');
      setShowReplyForm(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div 
      className={`p-4 rounded-xl border ${
        comment.status === 'pending_review' 
          ? 'border-yellow-200 bg-yellow-50' 
          : 'border-gray-100 bg-white'
      }`}
      data-testid={`comment-${comment.id}`}
    >
      {/* Cabeçalho */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-[var(--primary)]/10 flex items-center justify-center flex-shrink-0">
            {comment.author_avatar ? (
              <img 
                src={comment.author_avatar} 
                alt={comment.author_name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <User className="w-5 h-5 text-[var(--primary)]" />
            )}
          </div>
          
          <div>
            <p className="font-semibold text-gray-900 text-sm">{comment.author_name}</p>
            <TimeAgo date={comment.created_at} />
          </div>
        </div>

        {/* Menu de opções */}
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 hover:bg-gray-100 rounded-full"
            data-testid={`comment-menu-${comment.id}`}
          >
            <MoreVertical className="w-4 h-4 text-gray-400" />
          </button>
          
          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 min-w-[150px]">
                {!isAuthor && (
                  <button
                    onClick={() => { onReport(comment); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                    data-testid={`report-comment-${comment.id}`}
                  >
                    <Flag className="w-4 h-4" />
                    Denunciar
                  </button>
                )}
                {isAuthor && (
                  <button
                    onClick={() => { onDelete(comment.id); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                    data-testid={`delete-comment-${comment.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                    Excluir
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Status de moderação */}
      {comment.status === 'pending_review' && (
        <div className="mt-2 flex items-center gap-2 text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded">
          <AlertTriangle className="w-3 h-3" />
          <span>Comentário em análise</span>
        </div>
      )}

      {/* Conteúdo do comentário */}
      <p className="mt-3 text-gray-700 text-sm leading-relaxed whitespace-pre-line">
        {comment.content}
      </p>

      {/* Ações */}
      <div className="mt-3 flex items-center gap-4">
        {/* Like */}
        <button
          onClick={handleLikeToggle}
          className={`flex items-center gap-1.5 text-sm transition-colors ${
            isLiked 
              ? 'text-[var(--primary)] font-medium' 
              : 'text-gray-500 hover:text-[var(--primary)]'
          }`}
          data-testid={`like-comment-${comment.id}`}
        >
          <ThumbsUp className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
          <span>{comment.likes_count || 0}</span>
        </button>

        {/* Responder (só para owner do negócio) */}
        {isOwner && !comment.owner_reply && (
          <button
            onClick={() => setShowReplyForm(!showReplyForm)}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[var(--primary)]"
            data-testid={`reply-comment-${comment.id}`}
          >
            <Reply className="w-4 h-4" />
            <span>Responder</span>
          </button>
        )}
      </div>

      {/* Formulário de resposta */}
      {showReplyForm && (
        <form onSubmit={handleSubmitReply} className="mt-3 pl-4 border-l-2 border-[var(--primary)]/30">
          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Escreva sua resposta como proprietário..."
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] resize-none"
            data-testid={`reply-input-${comment.id}`}
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              type="submit"
              disabled={!replyContent.trim() || submitting}
              className="px-3 py-1.5 bg-[var(--primary)] text-white text-sm rounded-lg hover:bg-[var(--primary-dark)] disabled:opacity-50 flex items-center gap-1"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Responder
            </button>
            <button
              type="button"
              onClick={() => setShowReplyForm(false)}
              className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Resposta do proprietário */}
      {comment.owner_reply && (
        <div className="mt-3 pl-4 border-l-2 border-[var(--secondary)] bg-[var(--secondary)]/5 rounded-r-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-[var(--secondary)]">
              Resposta do proprietário
            </span>
            {comment.owner_reply_at && <TimeAgo date={comment.owner_reply_at} />}
          </div>
          <p className="text-sm text-gray-700">{comment.owner_reply}</p>
        </div>
      )}
    </div>
  );
};

// Componente principal da seção de comentários
export default function CommentSection({ businessId, businessOwnerId }) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [comments, setComments] = useState([]);
  const [stats, setStats] = useState({ total: 0, average_rating: 0 });
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Report modal
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportingComment, setReportingComment] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [reportSuccess, setReportSuccess] = useState(false);
  const [submittingReport, setSubmittingReport] = useState(false);

  // Modal de login inline — mantém o usuário na mesma página. Evita redirect
  // para /perfil que fazia o usuário perder o contexto da avaliação/comentário.
  const [showAuthModal, setShowAuthModal] = useState(false);

  const isOwner = user?.id === businessOwnerId;

  // Carregar comentários
  const fetchComments = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/v2/comments/business/${businessId}`);
      // V2 retorna { comments, total, rating_stats: { average, total, distribution } }
      setComments(res.data.comments || []);
      setStats({
        total: res.data.total || res.data.rating_stats?.total || 0,
        average_rating: res.data.rating_stats?.average || 0,
      });
    } catch (err) {
      console.error('Erro ao carregar comentários:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId) {
      fetchComments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  // Postar novo comentário
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await api.post(
        `/v2/comments/business/${businessId}`,
        { content: newComment }
      );
      setNewComment('');
      fetchComments();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao enviar comentário';
      alert(detail);
    } finally {
      setSubmitting(false);
    }
  };

  // Curtir comentário
  const handleLike = async (commentId) => {
    if (!user) {
      navigate('/perfil');
      return;
    }
    
    try {
      await api.post(`/v2/comments/${commentId}/like`);
      setComments(prev => prev.map(c => 
        c.id === commentId 
          ? { ...c, likes_count: (c.likes_count || 0) + 1, liked_by: [...(c.liked_by || []), user.id] }
          : c
      ));
    } catch (err) {
      console.error('Erro ao curtir:', err);
    }
  };

  // Descurtir comentário
  const handleUnlike = async (commentId) => {
    try {
      await api.delete(`/v2/comments/${commentId}/like`);
      setComments(prev => prev.map(c => 
        c.id === commentId 
          ? { 
              ...c, 
              likes_count: Math.max(0, (c.likes_count || 0) - 1), 
              liked_by: (c.liked_by || []).filter(id => id !== user.id) 
            }
          : c
      ));
    } catch (err) {
      console.error('Erro ao descurtir:', err);
    }
  };

  // Responder comentário (como proprietário)
  const handleReply = async (commentId, content) => {
    try {
      await api.post(
        `/v2/comments/${commentId}/reply`,
        { content }
      );
      fetchComments();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao responder comentário';
      alert(detail);
    }
  };

  // Abrir modal de denúncia
  const handleOpenReport = (comment) => {
    setReportingComment(comment);
    setShowReportModal(true);
    setReportReason('');
    setReportDescription('');
    setReportSuccess(false);
  };

  // Enviar denúncia
  const handleSubmitReport = async (e) => {
    e.preventDefault();
    
    if (!reportReason) {
      alert('Selecione um motivo para a denúncia');
      return;
    }

    setSubmittingReport(true);
    try {
      await api.post(
        '/reports',
        {
          entity_type: 'comment',
          entity_id: reportingComment.id,
          reason: reportReason,
          description: reportDescription,
          evidence_urls: []
        }
      );
      setReportSuccess(true);
      setTimeout(() => {
        setShowReportModal(false);
        setReportingComment(null);
      }, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao enviar denúncia';
      alert(detail);
    } finally {
      setSubmittingReport(false);
    }
  };

  // Excluir comentário próprio
  const handleDelete = async (commentId) => {
    if (!window.confirm('Tem certeza que deseja excluir este comentário?')) return;
    
    try {
      await api.delete(`/v2/comments/${commentId}`);
      fetchComments();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao excluir comentário';
      alert(detail);
    }
  };

  return (
    <div className="p-6" id="comentarios" data-testid="comment-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <MessageCircle className="w-5 h-5 text-[var(--primary)]" />
          Comentários
          {stats.total > 0 && (
            <span className="text-sm font-normal text-gray-500">({stats.total})</span>
          )}
        </h2>
      </div>

      {/* Formulário de novo comentário */}
      <form onSubmit={handleSubmitComment} className="mb-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[var(--primary)]/10 flex items-center justify-center flex-shrink-0">
            {user?.picture ? (
              <img src={user.picture} alt={user.nome} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <User className="w-5 h-5 text-[var(--primary)]" />
            )}
          </div>
          <div className="flex-1">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={user ? "Deixe seu comentário..." : "Faça login para comentar"}
              rows={3}
              disabled={!user}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] resize-none disabled:bg-gray-50 disabled:cursor-not-allowed"
              data-testid="new-comment-input"
            />
            <div className="flex justify-end mt-2">
              {user ? (
                <button
                  type="submit"
                  disabled={!newComment.trim() || submitting}
                  className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary-dark)] disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
                  data-testid="submit-comment-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Publicar
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowAuthModal(true)}
                  className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary-dark)] text-sm font-medium"
                  data-testid="login-to-comment-btn"
                >
                  Fazer login para comentar
                </button>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* Lista de comentários */}
      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)] mx-auto" />
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <MessageCircle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p>Seja o primeiro a comentar!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              isOwner={isOwner}
              currentUserId={user?.id}
              onLike={handleLike}
              onUnlike={handleUnlike}
              onReply={handleReply}
              onReport={handleOpenReport}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Modal de denúncia de comentário */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {reportSuccess ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Denúncia Enviada</h3>
                <p className="text-gray-600">
                  Obrigado por nos ajudar a manter a qualidade da plataforma.
                </p>
              </div>
            ) : (
              <>
                <div className="p-6 border-b flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <Flag className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">Denunciar Comentário</h3>
                      <p className="text-sm text-gray-500">{reportingComment?.author_name}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowReportModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmitReport} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Motivo da denúncia *
                    </label>
                    <div className="space-y-2">
                      {COMMENT_REPORT_REASONS.map(reason => (
                        <label
                          key={reason.value}
                          className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                            reportReason === reason.value
                              ? 'border-red-500 bg-red-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="reason"
                            value={reason.value}
                            checked={reportReason === reason.value}
                            onChange={(e) => setReportReason(e.target.value)}
                            className="sr-only"
                          />
                          <span className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                            reportReason === reason.value
                              ? 'border-red-500 bg-red-500'
                              : 'border-gray-300'
                          }`}>
                            {reportReason === reason.value && (
                              <span className="w-2 h-2 bg-white rounded-full" />
                            )}
                          </span>
                          <span className="text-sm">{reason.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Detalhes adicionais (opcional)
                    </label>
                    <textarea
                      value={reportDescription}
                      onChange={(e) => setReportDescription(e.target.value)}
                      placeholder="Descreva mais detalhes sobre o problema..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowReportModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={!reportReason || submittingReport}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {submittingReport ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Flag className="w-4 h-4" />
                      )}
                      Enviar Denúncia
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de login inline — não navega, mantém usuário na página do negócio */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => setShowAuthModal(false)}
      />
    </div>
  );
}
